import { DataTable, type Column, fmt, FilterBlock } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useMemo, useState } from "react";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useListContractsQuery } from "~/shared/api/contract/contract.grpc";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

import { normalizeForSearch } from "@racoon/lib";
export default function ContractsPage() {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "contracts",
    selectedCompanyId || undefined,
  );
  const [searchValue, setSearchValue] = useState("");

  const { data, isLoading } = useListContractsQuery(
    { targetOrganizationIds: [BigInt(selectedCompanyId || 0)] },
    { skip: !selectedCompanyId },
  );

  const rows = useMemo<ConnectProto.Contract[]>(() => {
    const list = data?.contracts ?? [];
    const term = normalizeForSearch(searchValue);
    if (!term) return list;
    return list.filter(
      (x) =>
        normalizeForSearch(x.dealNumber).includes(term) ||
        normalizeForSearch(x.serviceProvider?.name).includes(term),
    );
  }, [data, searchValue]);

  const columns = useMemo<Column<ConnectProto.Contract>[]>(() => {
    return [
      {
        id: "index",
        header: "#",
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "deal_number",
        header: "Номер договора",
        accessor: (r) => r.dealNumber,
        sortable: true,
        width: 200,
      },
      {
        id: "start_at",
        header: "Дата начала",
        accessor: (r) => r.startAt,
        sortable: true,
        width: 180,
        cell: (info) => {
          const v = info.getValue() as ConnectProto.Contract["startAt"] | undefined;
          return v ? fmt.dateTime(new Date(Number(v.seconds) * 1000)) : "";
        },
      },
      {
        id: "end_at",
        header: "Дата окончания",
        accessor: (r) => r.endAt,
        sortable: true,
        width: 180,
        cell: (info) => {
          const v = info.getValue() as ConnectProto.Contract["endAt"] | undefined;
          return v ? fmt.dateTime(new Date(Number(v.seconds) * 1000)) : "";
        },
      },
      {
        id: "status",
        header: "Статус",
        accessor: (r) => r.status,
        width: 160,
        sortable: true,
        cell: (info) => {
          const v = info.getValue() as ConnectProto.ContractStatus | undefined;
          return v === ConnectProto.ContractStatus.ACTIVE ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 text-xs px-2 py-1">
              Активен
            </span>
          ) : v === ConnectProto.ContractStatus.INACTIVE ? (
            <span className="inline-flex items-center rounded-full bg-slate-200 text-slate-700 text-xs px-2 py-1">
              Архивирован
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-yellow-100 text-yellow-700 text-xs px-2 py-1">
              {v ?? "Неизвестно"}
            </span>
          );
        },
      },
      {
        id: "tariff",
        header: "Тариф",
        accessor: (r) => r.tariff,
        sortable: true,
        width: 120,
        cell: (info) => {
          const v = info.getValue() as bigint | undefined;
          return v ? `${Number(v)} ₽` : "-";
        },
      },
    ];
  }, []);

  if (!selectedCompanyId) {
    return <div>{t(Namespace.Organizations, "selectOrganization")}</div>;
  }

  return (
    <div className="min-h-screen w-full flex flex-col space-y-4">
      <FilterBlock
        onSearchChange={setSearchValue}
        searchValue={searchValue}
        totalCount={rows.length}
        itemType="contracts"
        direction="row"
      />

      <DataTable<ConnectProto.Contract>
        columns={columns}
        data={rows}
        loading={isLoading}
        initialSort={{ id: "deal_number", dir: "asc" }}
        rowKey={(r) => String(r.id)}
        selectable={false}
        caption={null}
        zebra
        className="min-w-[100%]"
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Table}
      />
    </div>
  );
}
